import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, ArrowRight, BedDouble } from 'lucide-react';
import type { AdmissionTransferDto } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Input, Select, Spinner } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ShellEmpty, ShellPanel, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { admissionIsOpen, type Admission } from '../types';
import { admissionKey } from '../queries';
import { bedClassLabel } from '../../bed-board-look';

export function transfersKey(admissionId: string) {
  return ['admission-transfers', admissionId] as const;
}

function bedText(bed: AdmissionTransferDto['to']): string {
  return bed ? `${bed.ward} · ${bed.code}` : '—';
}

export function BedSection({ admission }: { admission: Admission }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [transferring, setTransferring] = useState(false);
  const current = admissionIsOpen(admission);
  const transfers = useQuery({
    queryKey: transfersKey(admission.id),
    queryFn: () => get<AdmissionTransferDto[]>(`/ipd/admissions/${admission.id}/transfers`),
  });
  const mayTransfer = current && can('admission.manage', 'bed.manage');

  return (
    <>
      <ShellSectionHeading
        icon={BedDouble}
        title={tr('ipd', 'ws_nav_bed')}
        description={tr('ipd', 'ws_bed_desc')}
        actions={
          mayTransfer ? (
            <Button size="sm" variant="outline" onClick={() => setTransferring(true)}>
              <ArrowLeftRight className="h-4 w-4" aria-hidden /> {tr('ipd', 'transfer_bed')}
            </Button>
          ) : null
        }
      />
      <ShellPanel icon={BedDouble} title={tr('ipd', 'ws_current_bed')}>
        {admission.bed ? (
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">{tr('ipd', 'ws_ward')}</dt>
              <dd className="font-medium">{admission.bed.room.ward.name}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{tr('ipd', 'ws_room_bed')}</dt>
              <dd className="font-medium" dir="ltr">
                {admission.bed.room.code} · {admission.bed.code}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{tr('ipd', 'ws_bed_class')}</dt>
              <dd>
                <Badge variant="secondary">{bedClassLabel(admission.bed.bedClass)}</Badge>
              </dd>
            </div>
          </dl>
        ) : (
          <ShellEmpty>{tr('ipd', 'ws_no_bed')}</ShellEmpty>
        )}
        {admission.bedChargeConfigured === false && current && (
          <Alert variant="warning" className="mt-3" title={tr('ipd', 'this_bed_has_no_bed_day_charge')}>
            {tr('ipd', 'nothing_has_been_posted_for_the_stay_itself_set')}
          </Alert>
        )}
      </ShellPanel>
      <ShellPanel icon={ArrowLeftRight} title={tr('ipd', 'ws_transfer_history')}>
        {transfers.isLoading && <Spinner />}
        {transfers.isError && <Alert variant="destructive">{describeError(transfers.error)}</Alert>}
        {transfers.data && transfers.data.length === 0 && <ShellEmpty>{tr('ipd', 'ws_no_transfers')}</ShellEmpty>}
        <ol className="space-y-2">
          {transfers.data?.map((move) => (
            <li key={move.id} className="rounded-xl border px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground">{move.from ? bedText(move.from) : tr('ipd', 'ws_admitted_to')}</span>
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground rtl:-scale-x-100" aria-hidden />
                <span className="font-medium">{bedText(move.to)}</span>
                <span className="ms-auto text-xs text-muted-foreground">{dateTime(move.transferredAt)}</span>
              </div>
              {(move.reason || move.by) && (
                <p className="mt-1 text-xs text-muted-foreground">{[move.reason, move.by ? tr('ipd', 'ws_by_x', { name: move.by }) : null].filter(Boolean).join(' · ')}</p>
              )}
            </li>
          ))}
        </ol>
      </ShellPanel>
      {transferring && (
        <TransferDialog
          admissionId={admission.id}
          onClose={() => setTransferring(false)}
          onSaved={() => {
            setTransferring(false);
            void queryClient.invalidateQueries({ queryKey: admissionKey(admission.id) });
            void queryClient.invalidateQueries({ queryKey: transfersKey(admission.id) });
            void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
          }}
        />
      )}
    </>
  );
}

export function TransferDialog({ admissionId, onClose, onSaved }: { admissionId: string; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [bedId, setBedId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const beds = useQuery({
    queryKey: ['vacant-beds', 'transfer'],
    queryFn: () => get<{ id: string; code: string; bedClass: string; room: { ward: { name: string } } }[]>('/ipd/beds/vacant'),
  });

  const transfer = useMutation({
    mutationFn: () => post(`/ipd/admissions/${admissionId}/transfer`, { toBedId: bedId, reason: reason || undefined }),
    onSuccess: () => {
      toast.success(tr('ipd', 'patient_transferred'), tr('ipd', 'the_new_bed_class_is_charged_from_now'));
      onSaved();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      icon={ArrowLeftRight}
      title={tr('ipd', 'transfer_bed')}
      description={tr('ipd', 'the_tariff_changes_from_the_moment_of_transfer')}
      dirty={bedId !== '' || reason !== ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              transfer.mutate();
            }}
            loading={transfer.isPending}
            disabled={!bedId}
          >
            {tr('ipd', 'transfer')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('ipd', 'destination_bed')} required>
          <Select value={bedId} onChange={(e) => setBedId(e.target.value)}>
            <option value="">{tr('ipd', 'select_a_free_bed')}</option>
            {beds.data?.map((bed) => (
              <option key={bed.id} value={bed.id}>
                {bed.room.ward.name} · {bed.code} ({bedClassLabel(bed.bedClass)})
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('ipd', 'reason')}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
